import { useEffect, useReducer } from 'react'

import type { Comment, PostCommentRequestBody } from 'services/comments/types'
import * as commentsApi from 'services/comments'

const START_POSTING = 'start-posting'
const START_FETCH = 'start-fetch'
const COMMENTS_FETCHED = 'comments-fetched'
const POSTED_COMMENT = 'posted-comment'
const POSTED_REPLY = 'posted-reply'
const EDITED_COMMENT = 'edited-comment'
const SORT = 'sort'
const FILTER_BY_TOPIC = 'filter-by-topic'

type SortOption = 'DESC' | 'ASC'

type State = {
  isLoading: boolean
  ids: string[]
  entities: { [key: string]: Comment }
  activeSort: SortOption
  filterByTopicId: string | undefined
}

type Action =
  | {
      type: typeof COMMENTS_FETCHED
      payload: Comment[]
    }
  | {
      type: typeof POSTED_COMMENT
      payload: Comment
    }
  | {
      type: typeof POSTED_REPLY
      payload: Comment
    }
  | {
      type: typeof EDITED_COMMENT
      payload: Comment
    }
  | { type: typeof START_FETCH }
  | { type: typeof START_POSTING }
  | {
      type: typeof SORT
      payload: SortOption
    }
  | {
      type: typeof FILTER_BY_TOPIC
      payload: string | undefined
    }

const initialState = {
  isLoading: true,
  ids: [],
  entities: {},
  activeSort: 'DESC' as const,
  filterByTopicId: undefined,
}

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case START_POSTING:
    case START_FETCH:
      return {
        ...state,
        isLoading: true,
      }

    case SORT:
      return {
        ...state,
        activeSort: action.payload,
      }

    case FILTER_BY_TOPIC:
      return {
        ...state,
        filterByTopicId: action.payload,
      }

    case EDITED_COMMENT:
    case POSTED_REPLY: {
      const comment = action.payload
      const { parentId } = comment
      if (parentId) {
        const parentComment = state.entities[parentId]

        return {
          ...state,
          entities: {
            ...state.entities,
            [parentId]: {
              ...parentComment,
              replies: [...parentComment.replies, comment],
            },
          },
          isLoading: false,
        }
      } else {
        const { id } = comment
        return {
          ...state,
          entities: {
            ...state.entities,
            [id]: { ...comment, replies: state.entities[id].replies },
          },
          isLoading: false,
        }
      }
    }

    case POSTED_COMMENT: {
      const comment = action.payload
      const { id } = comment

      return {
        ...state,
        ids: [id, ...state.ids],
        entities: {
          ...state.entities,
          [id]: comment,
        },
        isLoading: false,
      }
    }

    case COMMENTS_FETCHED: {
      const { ids, entities } = action.payload.reduce(
        (acc, comment) => {
          const { id } = comment
          acc.ids.push(id)
          acc.entities[id] = comment
          return acc
        },
        {
          ids: [...state.ids],
          entities: { ...state.entities },
        },
      )
      return {
        ...state,
        ids,
        entities,
        isLoading: false,
      }
    }
  }
}

function getComments(state: State) {
  let comments = state.ids.map((ids) => state.entities[ids])

  if (state.filterByTopicId) {
    comments = comments.filter(
      (comment) => comment.topicId === state.filterByTopicId,
    )
  }

  if (state.activeSort === 'ASC') {
    comments = comments.reverse()
  }

  return comments
}

function getCount(comments: Comment[]) {
  return comments.reduce((acc, comment) => {
    return acc + 1 + comment.replies.length
  }, 0)
}

function getIsLoading(state: State) {
  return state.isLoading
}

function getActiveSort(state: State) {
  return state.activeSort
}

type Props = {
  topicIds: string[]
  selectedTopicId?: string
}

function useComments({ topicIds, selectedTopicId }: Props) {
  const [state, dispatch] = useReducer(reducer, initialState)

  async function fetch() {
    dispatch({
      type: START_FETCH,
    })

    const response = await commentsApi.fetchComments(topicIds)

    dispatch({
      type: COMMENTS_FETCHED,
      payload: response,
    })
  }

  async function handleSubmitPost(post: PostCommentRequestBody) {
    dispatch({
      type: START_POSTING,
    })
    const response = await commentsApi.postComment(post)

    dispatch({
      type: POSTED_COMMENT,
      payload: response,
    })
  }

  async function handleSubmitReply(post: PostCommentRequestBody) {
    dispatch({
      type: START_POSTING,
    })

    const response = await commentsApi.postReply(post)

    dispatch({
      type: POSTED_REPLY,
      payload: response,
    })
  }

  async function handleSubmitEdit(post: PostCommentRequestBody) {
    dispatch({
      type: START_POSTING,
    })

    const response = await commentsApi.editComment(post)

    dispatch({
      type: EDITED_COMMENT,
      payload: response,
    })
  }

  useEffect(() => {
    fetch()
  }, [])

  function handleSort(sort: SortOption) {
    dispatch({
      type: SORT,
      payload: sort,
    })
  }

  useEffect(() => {
    dispatch({
      type: FILTER_BY_TOPIC,
      payload: selectedTopicId,
    })
  }, [selectedTopicId])

  const comments = getComments(state)

  return {
    comments,
    count: getCount(comments),
    isLoading: getIsLoading(state),
    activeSort: getActiveSort(state),
    onSort: handleSort,
    onSubmitPost: handleSubmitPost,
    onSubmitReply: handleSubmitReply,
    onSubmitEdit: handleSubmitEdit,
  }
}

export default useComments
